<template>
  <div>
    <Spinner v-if="isLoading" />
    <div v-else>
      <RegistrationLayout>
        <template v-slot:header>
          <HMButton
            color="white"
            rounded="full"
            size="x-small"
            fontWeight="semibold"
            textSize="small"
            padding="tight"
            @click="back"
          >
            <span>
              <Icon :iconClass="'fas fa-chevron-left'" />
              {{ $t('common.back_btn') }}
            </span>
          </HMButton>
        </template>
        <template v-slot:default>
          <ModalitySelect
            v-if="step === 1"
            :errorText="errorText"
            :helperText="
              coalescedSupportText(
                'common.contact_health_plan_csr.contact_info_change'
              )
            "
            :mfaPreference="mfaType"
            :availableMfaModalities="availableMfaModalities"
            @continue="onMfaSelect"
          />
          <VerifyIdentityMfa
            v-if="step === 2"
            :mfaPreference="mfaType"
            :displayCheck="displayCheck"
            :displayCodeResend="displayCodeResend"
            :displayCodeSent="displayCodeSent"
            :displayErrorPage="displayErrorPage"
            :displayExplanation="displayExplanation"
            :displayMfaCodeError="displayMfaCodeError"
            :helperText="
              coalescedSupportText(
                'common.contact_health_plan_csr.contact_info_change'
              )
            "
            @requestMfaCode="onRequestMfaCode"
            @verifyMfaCode="onSubmitMfaCode"
            @cancel="handleReturn"
          />
        </template>
      </RegistrationLayout>
    </div>
  </div>
</template>

<script>
import ModalitySelect from '../../common/mfa/ModalitySelect.vue'
import VerifyIdentityMfa from '../../common/mfa/VerifyIdentityMfa.vue'
import Spinner from '@components/common/Spinner'
import { getMaskedContactInfo } from '@platform-shared/services/member'
import login from '@platform-shared/mixins/login'
import { MFA_TYPES } from '@platform-shared/constants'
import { formatPhoneNumber } from '@platform-shared/utils/formatters'
import supportMixin from '../../mixins/support'
import {
  TOO_MANY_CHECK_ATTEMPTS,
  TOO_MANY_SEND_ATTEMPTS,
} from '@platform-shared/verify-constants'
import RegistrationLayout from '@components/layouts/RegistrationLayout'
import { mapActions } from 'vuex'

export default {
  components: {
    ModalitySelect,
    VerifyIdentityMfa,
    Spinner,
    RegistrationLayout,
  },
  mixins: [login, supportMixin],
  data() {
    return {
      step: 1,
      isLoading: false,
      displayErrorPage: false,
      errorText: '',
      displayMfaCodeError: false,
      displayCodeResend: false,
      maskedContactInfo: {},
    }
  },
  computed: {
    availableMfaModalities() {
      let availableModalities = []

      Object.values(MFA_TYPES).forEach((mfaType) => {
        switch (mfaType) {
          case MFA_TYPES.SMS:
            if (this.maskedContactInfo.primarySmsPhone)
              availableModalities.push({
                type: mfaType,
                value: formatPhoneNumber(
                  this.maskedContactInfo.primarySmsPhone
                ),
              })
            break
          case MFA_TYPES.VOICE:
            if (this.maskedContactInfo.primaryPhone)
              availableModalities.push({
                type: mfaType,
                value: formatPhoneNumber(this.maskedContactInfo.primaryPhone),
              })
            break
          case MFA_TYPES.EMAIL:
            if (this.maskedContactInfo.primaryEmail)
              availableModalities.push({
                type: mfaType,
                value: this.maskedContactInfo.primaryEmail,
              })
            break
        }
      })
      return availableModalities
    },
    displayCheck() {
      return this.mfaType === MFA_TYPES.EMAIL
        ? this.$t('mfa.check_email')
        : this.$t('mfa.check_phone')
    },
    displayCodeSent() {
      const result = this.availableMfaModalities.find(
        (x) => x.type === this.mfaType
      )?.value
      return result ? this.$t('mfa.code_sent', [result]) : ''
    },
    displayExplanation() {
      return this.mfaType === MFA_TYPES.EMAIL
        ? this.$t('mfa.explanation_email')
        : this.$t('mfa.explanation_phone')
    },
  },
  async created() {
    try {
      this.isLoading = true
      const result = await getMaskedContactInfo()
      this.maskedContactInfo = result.data

      if (Object.keys(this.maskedContactInfo).length === 0) {
        this.$router.replace('/login-verify-error')
      }
      this.isLoading = false
    } catch {
      this.isLoading = false
    }
  },
  methods: {
    ...mapActions(['logout']),
    onMfaSelect(mfaPreference) {
      this.updateMfaPreference(mfaPreference)
        .then(() => {
          this.step = 2
        })
        .catch((error) => {
          this.errorText = error.json.message
        })
    },
    onRequestMfaCode() {
      this.requestMfaCode()
        .then(() => {
          this.displayCodeResend = true
          setTimeout(() => {
            this.displayCodeResend = false
          }, 5000)
        })
        .catch((e) => {
          if (e.message === TOO_MANY_SEND_ATTEMPTS) {
            this.displayErrorPage = true
          }
        })
    },
    onSubmitMfaCode(totp) {
      this.mfaCodeError = false
      this.verifyMfaCode(totp)
        .then(this.initData)
        .catch((e) => {
          if (e.message === TOO_MANY_CHECK_ATTEMPTS) {
            this.displayErrorPage = true
          } else {
            this.displayMfaCodeError = true
          }
        })
    },
    handleReturn() {
      this.logout()
    },
    back() {
      if (this.step === 1) {
        this.logout()
      } else {
        this.step--
      }
    },
  },
}
</script>
